import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {
  GridCsvExportMenuItem,
  GridCsvExportOptions,
  GridToolbarExportContainer,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useExcelExport } from "@/hooks/export/useExcelExport";
import { Report } from "@/utilities/export/report";
import { EXCLUDED_EXPORT_FIELDS, getExportedFields, gridTable } from "@/utilities/export/table";

export interface GridExportMenuProps {
  report: Omit<Report, "table">;
  fractionDigits?: Record<string, number>;
  disabled?: boolean;
  csvOptions?: GridCsvExportOptions;
  /** Include columns hidden in the grid, in both the CSV and the Excel export. */
  allColumns?: boolean;
  /**
   * Columns exported even when hidden in the grid (e.g. licence plate and VIN on a logbook that
   * is handed on), in both the CSV and the Excel export.
   */
  forcedFields?: string[];
}

/**
 * CSV options for every grid export. The UTF-8 BOM is on by default: without it Excel opens the
 * file as ANSI and garbles umlauts. Explicit `csvOptions` win.
 */
export function csvExportOptions(
  allColumns?: boolean,
  csvOptions?: GridCsvExportOptions,
): GridCsvExportOptions {
  return { utf8WithBom: true, ...(allColumns ? { allColumns: true } : {}), ...csvOptions };
}

export function GridExportMenu({
  report,
  fractionDigits,
  disabled,
  csvOptions,
  allColumns,
  forcedFields,
}: GridExportMenuProps) {
  const hasForcedFields = !!forcedFields && forcedFields.length > 0;
  return (
    <GridToolbarExportContainer slotProps={{ button: { disabled } }}>
      {hasForcedFields ? (
        <ForcedFieldsCsvMenuItem
          allColumns={allColumns}
          csvOptions={csvOptions}
          forcedFields={forcedFields}
        />
      ) : (
        <GridCsvExportMenuItem options={csvExportOptions(allColumns, csvOptions)} />
      )}
      <ExcelExportMenuItem
        report={report}
        fractionDigits={fractionDigits}
        allColumns={allColumns}
        forcedFields={forcedFields}
      />
    </GridToolbarExportContainer>
  );
}

interface ForcedFieldsCsvMenuItemProps {
  allColumns?: boolean;
  csvOptions?: GridCsvExportOptions;
  forcedFields: string[];
  /** Injected by `GridToolbarExportContainer`. */
  hideMenu?: () => void;
}

/**
 * CSV item that adds the forced fields to the visible ones. The field list is read at click time,
 * so it follows the column visibility the user has set by then.
 */
function ForcedFieldsCsvMenuItem({
  allColumns,
  csvOptions,
  forcedFields,
  hideMenu,
}: ForcedFieldsCsvMenuItemProps) {
  const apiRef = useGridApiContext();

  const onClick = () => {
    const fields = getExportedFields(apiRef, { allColumns, forcedFields });
    apiRef.current.exportDataAsCsv({ ...csvExportOptions(allColumns, csvOptions), fields });
    hideMenu?.();
  };

  return <MenuItem onClick={onClick}>{apiRef.current.getLocaleText("toolbarExportCSV")}</MenuItem>;
}

interface ExcelExportMenuItemProps {
  report: Omit<Report, "table">;
  fractionDigits?: Record<string, number>;
  allColumns?: boolean;
  forcedFields?: string[];
  /** Injected by `GridToolbarExportContainer`. */
  hideMenu?: () => void;
}

function ExcelExportMenuItem({
  report,
  fractionDigits,
  allColumns,
  forcedFields,
  hideMenu,
}: ExcelExportMenuItemProps) {
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const { exportExcel } = useExcelExport();
  const { enqueueSnackbar } = useSnackbar();

  const excludedHeaders = apiRef.current
    .getAllColumns()
    .filter(column => EXCLUDED_EXPORT_FIELDS.has(column.field))
    .map(column => column.headerName ?? column.field);

  const onClick = () => {
    try {
      // Read synchronously, before anything async, so the table matches what the user saw.
      void exportExcel({ ...report, table: gridTable(apiRef, { fractionDigits, allColumns, forcedFields }) });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("common.exportError"), { variant: "error" });
    } finally {
      hideMenu?.();
    }
  };

  const item = <MenuItem onClick={onClick}>{t("export.excel")}</MenuItem>;
  if (excludedHeaders.length === 0) return item;

  return (
    <Tooltip
      title={t("export.ignoredColumnsTooltip", { columns: excludedHeaders.join(", ") })}
      placement="right"
    >
      {item}
    </Tooltip>
  );
}
